/**
 * Small pieces the plugin pages share: the secret shown once, the schema list,
 * the "where it runs" editor, and key: value lines.
 */
import { useEffect, useMemo, useState } from 'react';
import { SegmentedControl } from '@ofself/shell';

import api from '../../services/api';
import { copyText } from '../../utils/clipboard';
import { Button, useToast } from '../../components/common';
import { KINDS } from './manifest';

/* ── key: value lines ─────────────────────────────────────────────────────── */

export const objToLines = (obj) => Object.entries(obj || {})
  .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`).join('\n');

export function linesToObj(text) {
  const out = {};
  for (const line of String(text || '').split('\n')) {
    const i = line.indexOf(':');
    if (i <= 0) continue;
    const k = line.slice(0, i).trim();
    if (k) out[k] = line.slice(i + 1).trim();
  }
  return out;
}

/* ── the schemas a picker offers, by name ─────────────────────────────────── */

let schemasOnce = null;

/** `{list, nameOf(id)}`: every schema the developer can use, newest version. */
export function useSchemas() {
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    schemasOnce = schemasOnce || api.request('/schemas?limit=1000&group_by_uri=true')
      .then((r) => (r?.schemas || [])
        .filter((s) => s.is_active !== false && !s.deleted_at)
        .map((s) => ({
          id: s.id,
          ids: s.version_ids || [s.id],
          schema_name: s.schema_name,
          display: s.schema_metadata?.display_name || s.schema_name,
        }))
        .sort((a, b) => a.display.localeCompare(b.display)))
      .catch(() => { schemasOnce = null; return []; });
    schemasOnce.then((l) => { if (live) { setList(l); setLoaded(true); } });
    return () => { live = false; };
  }, []);
  const nameOf = useMemo(() => {
    const by = new Map();
    for (const s of list) for (const id of s.ids) by.set(String(id), s);
    return (id) => (id ? by.get(String(id)) || null : null);
  }, [list]);
  return { list, loaded, find: nameOf, nameOf: (id) => nameOf(id)?.display || null };
}

/** A schema picked by name; stores the id. */
export function SchemaPicker({ value, onChange, schemas, disabled, anyLabel = 'Any schema', id }) {
  const known = schemas.find(value);
  // A stored id of an older version shows as its schema, and stays as stored
  // until someone picks another.
  const shown = known ? known.id : (value || '');
  return (
    <select id={id} className="pw__in" value={shown} disabled={disabled}
            onChange={(e) => onChange(e.target.value || null)}>
      <option value="">{anyLabel}</option>
      {value && !known ? (
        <option value={value}>{schemas.loaded ? 'A schema that no longer exists' : '…'}</option>
      ) : null}
      {schemas.list.map((s) => (
        <option key={s.id} value={s.id}>
          {s.display}{s.display !== s.schema_name ? ` · ${s.schema_name}` : ''}
        </option>
      ))}
    </select>
  );
}

/* ── the signing secret, once ─────────────────────────────────────────────── */

export function SecretOnce({ secret, title = 'Signing secret', onDone, doneLabel = 'Done' }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (await copyText(secret)) {
      setCopied(true);
      toast.success('Copied.');
    } else {
      toast.error('Could not copy. Select it and copy by hand.');
    }
  };
  return (
    <section className="pw__secret" aria-live="polite">
      <h2 className="pw__h">{title}</h2>
      <p className="pw__line"><strong>Save it now. It isn't shown again.</strong></p>
      <div className="pw__secret-row">
        <code className="pw__secret-value">{secret}</code>
        <Button variant="secondary" size="sm" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
      </div>
      <p className="pw__line pw__muted">Every call ofSelf makes to your plugin is signed with it.</p>
      {onDone ? (
        <div className="pw__actions">
          <Button onClick={onDone} disabled={!copied}>{doneLabel}</Button>
          {!copied ? <span className="pw__muted pw__small">Copy it first.</span> : null}
        </div>
      ) : null}
    </section>
  );
}

/* ── where it runs ────────────────────────────────────────────────────────── */

export function KindPicker({ value, onChange, disabled }) {
  return (
    <SegmentedControl
      options={KINDS.map((k) => ({ value: k.id, label: k.label }))}
      value={value}
      onChange={(v) => { if (!disabled) onChange(v); }}
    />
  );
}

/**
 * Where the plugin runs, for its kind: an endpoint (and a base URL for tools
 * with their own path), an MCP server (with Discover), or a declared request.
 * `draft` holds the manifest keys; `set(patch)` edits them.
 */
export function WhereItRuns({ draft, set, disabled, errors = [], onDiscovered }) {
  const toast = useToast();
  const [discovering, setDiscovering] = useState(false);
  const errAt = (k) => errors.filter((e) => e.field === k || e.field?.startsWith(`${k}.`)).map((e) => e.message);
  const t = draft.execution_type || 'endpoint';

  const discover = async () => {
    setDiscovering(true);
    try {
      const r = await api.mcpDiscover(draft.mcp_server_url.trim());
      const tools = r?.tools || [];
      onDiscovered?.(tools);
      toast.success(tools.length ? `Found ${tools.length} tool${tools.length === 1 ? '' : 's'}.` : 'The server lists no tools.');
    } catch (e) {
      toast.error(e.message || 'Could not reach the server.');
    }
    setDiscovering(false);
  };

  if (t === 'mcp') {
    return (
      <div className="pw__fields">
        <Field label="MCP server" errors={errAt('mcp_server_url')}>
          <div className="pw__inline">
            <input className="pw__in" value={draft.mcp_server_url} disabled={disabled}
                   placeholder="https://mcp.example.com/mcp"
                   onChange={(e) => set({ mcp_server_url: e.target.value })} />
            <Button variant="secondary" onClick={discover} loading={discovering}
                    disabled={disabled || !/^https?:\/\//.test(draft.mcp_server_url.trim())}>
              Discover
            </Button>
          </div>
        </Field>
      </div>
    );
  }

  if (t === 'request') {
    const spec = draft.request_spec || {};
    const map = draft.response_map || {};
    const setSpec = (patch) => set({ request_spec: { ...spec, ...patch } });
    const setMap = (patch) => {
      const next = { ...map, ...patch };
      for (const k of Object.keys(patch)) if (patch[k] === '' || patch[k] === undefined) delete next[k];
      set({ response_map: next });
    };
    return (
      <div className="pw__fields">
        <div className="pw__grid pw__grid--method">
          <Field label="Method">
            <select className="pw__in" value={spec.method || 'GET'} disabled={disabled}
                    onChange={(e) => setSpec({ method: e.target.value })}>
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="URL" errors={errAt('request_spec')}>
            <input className="pw__in" value={spec.url || ''} disabled={disabled}
                   placeholder="https://api.example.com/v1/items?user={{username}}"
                   onChange={(e) => setSpec({ url: e.target.value })} />
          </Field>
        </div>
        <div className="pw__grid">
          <Field label="Headers · one per line">
            <textarea className="pw__in pw__in--mono" rows={2} disabled={disabled}
                      defaultValue={objToLines(spec.headers)}
                      placeholder="Authorization: Bearer {{oauth_token}}"
                      onBlur={(e) => { if (e.target.value !== objToLines(spec.headers)) setSpec({ headers: linesToObj(e.target.value) }); }} />
          </Field>
          <Field label="Query · one per line">
            <textarea className="pw__in pw__in--mono" rows={2} disabled={disabled}
                      defaultValue={objToLines(spec.query)}
                      placeholder="window: {{window}}"
                      onBlur={(e) => { if (e.target.value !== objToLines(spec.query)) setSpec({ query: linesToObj(e.target.value) }); }} />
          </Field>
        </div>
        <h3 className="pw__h pw__h--sub">What comes back</h3>
        <div className="pw__grid">
          <Field label="Items" errors={errAt('response_map')}>
            <input className="pw__in pw__in--mono" value={map.items || ''} disabled={disabled}
                   placeholder="events[]" onChange={(e) => setMap({ items: e.target.value })} />
          </Field>
          <Field label="What makes each one unique">
            <input className="pw__in pw__in--mono" value={map.external_id || ''} disabled={disabled}
                   placeholder="id" onChange={(e) => setMap({ external_id: e.target.value })} />
          </Field>
        </div>
        <Field label="Title">
          <input className="pw__in pw__in--mono" value={map.title || ''} disabled={disabled}
                 placeholder="{{summary}}" onChange={(e) => setMap({ title: e.target.value })} />
        </Field>
        <Field label="Fields · node field: response path, one per line">
          <textarea className="pw__in pw__in--mono" disabled={disabled}
                    rows={Math.max(3, Object.keys(map.fields || {}).length)}
                    defaultValue={objToLines(map.fields)}
                    placeholder={'title: summary\nstart: start'}
                    onBlur={(e) => { if (e.target.value !== objToLines(map.fields)) setMap({ fields: linesToObj(e.target.value) }); }} />
        </Field>
      </div>
    );
  }

  return (
    <div className="pw__fields">
      <Field label="Endpoint" errors={errAt('endpoint_url')}>
        <input className="pw__in" value={draft.endpoint_url} disabled={disabled}
               placeholder="https://plugins.example.com/run"
               onChange={(e) => set({ endpoint_url: e.target.value })} />
      </Field>
      <Field label="Base URL · for tools with their own path" errors={errAt('plugin_endpoint_base_url')}>
        <input className="pw__in" value={draft.plugin_endpoint_base_url} disabled={disabled}
               placeholder="https://plugins.example.com"
               onChange={(e) => set({ plugin_endpoint_base_url: e.target.value })} />
      </Field>
    </div>
  );
}

/** A labelled control, with its errors beneath it. */
export function Field({ label, errors = [], children, htmlFor }) {
  return (
    <div className={`pw__f${errors.length ? ' is-bad' : ''}`}>
      {label ? <label className="pw__label" htmlFor={htmlFor}>{label}</label> : null}
      {children}
      {errors.map((m, i) => <span key={i} className="pw__err">{m}</span>)}
    </div>
  );
}
